import { Box, Pressable } from '../../mads';
import { Close } from '../../mads/icons';
import { useI18n } from '../../shared/I18n';

import { colors } from '@indeed/ifl-tokens';
import React, {
    createContext,
    useContext,
    useState,
    type ReactNode,
    useMemo,
    useCallback,
} from 'react';
import {
    Modal,
    StyleSheet,
    type DimensionValue,
    type GestureResponderEvent,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

interface ModalData {
    component: ReactNode;
    onDismiss?: () => void;
}

interface BottomSheetModalContextType {
    showModal: (props: { id: string; height: DimensionValue | undefined }) => void;
    hideModal: () => void;
    registerModal: (id: string, component: ReactNode, onDismiss?: () => void) => void;
    unregisterModal: (id: string) => void;
    updateHeight: (height: DimensionValue) => void;
}

const BottomSheetModalContext = createContext<BottomSheetModalContextType | undefined>(undefined);

export const useGlobalModal = () => {
    const context = useContext(BottomSheetModalContext);
    if (!context) {
        throw new Error('useGlobalModal must be used within a BottomSheetModalProvider');
    }
    return context;
};

export const BottomSheetModalProvider = ({ children }: { children: ReactNode }) => {
    const defaultHeight = '85%';
    const [visible, setVisible] = useState(false);
    const [activeModalId, setActiveModalId] = useState<string | null>(null);
    const [registeredModals, setRegisteredModals] = useState<Record<string, ModalData>>({});
    const [height, setHeight] = useState<DimensionValue | undefined>(defaultHeight);
    const i18n = useI18n();

    const registerModal = useCallback(
        (id: string, component: ReactNode, onDismiss?: () => void) => {
            setRegisteredModals((prev) => {
                // Only update if the component or callback is different
                if (prev[id]?.component === component && prev[id]?.onDismiss === onDismiss) {
                    return prev;
                }

                return {
                    ...prev,
                    [id]: { component, onDismiss },
                };
            });
        },
        [],
    );

    const unregisterModal = useCallback((id: string) => {
        setRegisteredModals((prev) => {
            const newModals = { ...prev };
            delete newModals[id];
            return newModals;
        });
    }, []);

    const showModal = useCallback(
        ({ id, height = defaultHeight }: { id: string; height: DimensionValue | undefined }) => {
            setHeight(height);
            if (registeredModals[id]) {
                setActiveModalId(id);
                setVisible(true);
            } else {
                console.warn(`Modal with id "${id}" is not registered`);
            }
        },
        [registeredModals],
    );

    const hideModal = useCallback(() => {
        // Call dismiss callback if one exists for the active modal
        if (activeModalId && registeredModals[activeModalId]?.onDismiss) {
            registeredModals[activeModalId]?.onDismiss?.();
        }
        setVisible(false);
        setActiveModalId(null);
    }, [activeModalId, registeredModals]);

    const updateHeight = useCallback((newHeight: DimensionValue) => {
        setHeight(newHeight);
    }, []);

    // Prevent presses in the modal's active content area from propagating to
    // the outer modal backdrop
    const handleContentPress = useCallback((e: GestureResponderEvent) => {
        e.stopPropagation();
    }, []);

    const activeContent = useMemo(() => {
        if (activeModalId && registeredModals[activeModalId]) {
            return registeredModals[activeModalId].component;
        }
        return null;
    }, [activeModalId, registeredModals]);

    const contextValue = useMemo(
        () => ({ showModal, hideModal, registerModal, unregisterModal, updateHeight }),
        [showModal, hideModal, registerModal, unregisterModal, updateHeight],
    );

    const styles = useMemo(
        () =>
            StyleSheet.create({
                visibleBackdrop: {
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(45,45,45,0.4)',
                },
                invisibleBackdrop: {
                    flex: 1,
                    backgroundColor: 'transparent',
                    justifyContent: 'flex-end',
                },
                modalContent: {
                    backgroundColor: 'white',
                    paddingHorizontal: 24,
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,
                    height: height,
                    width: '100%',
                    borderTopWidth: 1,
                    borderTopColor: colors.border.weak,
                },
                safeAreaContainer: {
                    flex: 1,
                },
                closeButton: {
                    padding: 8,
                },
                topBar: {
                    alignItems: 'flex-end',
                    marginTop: 8,
                    marginRight: -8,
                },
            }),
        [height],
    );

    return (
        <BottomSheetModalContext.Provider value={contextValue}>
            {children}
            {visible && <Box sx={styles.visibleBackdrop} />}
            <Modal
                animationType="slide"
                transparent
                visible={visible}
                onRequestClose={hideModal}
                supportedOrientations={[
                    'portrait',
                    'portrait-upside-down',
                    'landscape',
                    'landscape-left',
                    'landscape-right',
                ]}
            >
                <SafeAreaView style={styles.safeAreaContainer} edges={['left', 'right']}>
                    <Pressable
                        sx={styles.invisibleBackdrop}
                        onPress={hideModal}
                        accessible={false}
                    >
                        <Pressable
                            sx={styles.modalContent}
                            onPress={handleContentPress}
                            accessible={false}
                            accessibilityViewIsModal={true}
                            onAccessibilityEscape={hideModal}
                        >
                            <Pressable sx={styles.topBar} onPress={hideModal} accessible={false}>
                                <Pressable
                                    onPress={hideModal}
                                    hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
                                    sx={styles.closeButton}
                                    accessible={true}
                                    accessibilityRole="button"
                                    accessibilityLabel={i18n.gettext('Close modal')}
                                >
                                    <Close size="md" color={colors.foreground.strong} aria-hidden />
                                </Pressable>
                            </Pressable>
                            {activeContent}
                        </Pressable>
                    </Pressable>
                </SafeAreaView>
            </Modal>
        </BottomSheetModalContext.Provider>
    );
};
